import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Heading, Link, PageLoader, Paragraph, Stack } from '@customerio/pluma-components/react';
import { AuthFormLayout } from '@/components/auth-form-layout';
import { isEmptyString } from '@/utils/string';
import { fetchJoinRequestAccounts, signupResumeHref, type JoinRequestAccounts } from '@/services/join-request/api';
import { OrgPickerStep } from './steps/org-picker-step';
import { RequestSentStep } from './steps/request-sent-step';

type JoinRequestPageProps = {
  token: string | undefined;
};

type SentState = { accountName: string };

/**
 * Public "request to join your org" page, reached from the signup flow after
 * email verification when the verified email's domain matches an existing
 * account. The one-shot `token` query param authenticates every call; all its
 * failure modes (missing, expired, consumed) collapse into the link-expired
 * state, which points back at signup.
 */
export default function JoinRequestPage({ token: rawToken }: JoinRequestPageProps) {
  const [sent, setSent] = useState<SentState | null>(null);
  const [tokenExpired, setTokenExpired] = useState(false);

  // An empty `?token=` is as invalid as a missing one: normalize it away so
  // the page goes straight to the link-expired state instead of querying.
  const token = rawToken != null && !isEmptyString(rawToken) ? rawToken : undefined;

  const accountsQuery = useQuery({
    queryKey: ['join-request-accounts', token],
    queryFn: () => fetchJoinRequestAccounts(token ?? ''),
    enabled: token !== undefined && sent === null,
    staleTime: Infinity,
    retry: false,
  });

  return (
    <AuthFormLayout>
      <JoinRequestContent
        token={token}
        sent={sent}
        tokenExpired={tokenExpired}
        accountsQuery={accountsQuery}
        onSent={(accountName) => setSent({ accountName })}
        onTokenExpired={() => setTokenExpired(true)}
      />
    </AuthFormLayout>
  );
}

type JoinRequestContentProps = {
  token: string | undefined;
  sent: SentState | null;
  tokenExpired: boolean;
  accountsQuery: ReturnType<typeof useQuery<Awaited<ReturnType<typeof fetchJoinRequestAccounts>>>>;
  onSent: (accountName: string) => void;
  onTokenExpired: () => void;
};

/** The pre-data states: sent confirmation, no/expired token, loading, load error. */
function JoinRequestContent({
  token,
  sent,
  tokenExpired,
  accountsQuery,
  onSent,
  onTokenExpired,
}: JoinRequestContentProps) {
  if (sent !== null) {
    return <RequestSentStep accountName={sent.accountName} />;
  }

  if (token === undefined || tokenExpired) {
    return <LinkExpired />;
  }

  if (accountsQuery.isPending) {
    return <PageLoader label="Loading your organizations…" />;
  }

  if (accountsQuery.isError) {
    return (
      <Stack gap="200" data-test-join-request-error-state>
        <Heading level="1" size="m">
          Couldn't load organizations
        </Heading>
        <Paragraph color="subtle">We couldn't load your organizations. Refresh the page to try again.</Paragraph>
        <Link href="/signup">Continue creating a new account instead</Link>
      </Stack>
    );
  }

  return (
    <LoadedContent token={token} data={accountsQuery.data ?? null} onSent={onSent} onTokenExpired={onTokenExpired} />
  );
}

type LoadedContentProps = {
  token: string;
  /** Null when the token turned out invalid/expired/consumed (the fetch's 404 case). */
  data: JoinRequestAccounts | null;
  onSent: (accountName: string) => void;
  onTokenExpired: () => void;
};

/** The post-load states: invalid token (404 → null), org churn, or the picker. */
function LoadedContent({ token, data, onSent, onTokenExpired }: LoadedContentProps) {
  if (data === null) {
    return <LinkExpired />;
  }

  if (data.accounts.length === 0) {
    // Org churn between token mint and page load: nothing left to join.
    return (
      <Stack gap="200" data-test-join-request-no-match>
        <Heading level="1" size="m">
          No matching organization
        </Heading>
        <Paragraph color="subtle">
          We couldn't find a registered organization for your email domain anymore. You can continue creating a new
          account instead.
        </Paragraph>
        <Link href={signupResumeHref(data.email)}>Continue signing up</Link>
      </Stack>
    );
  }

  return <OrgPickerStep token={token} data={data} onSent={onSent} onTokenExpired={onTokenExpired} />;
}

function LinkExpired() {
  return (
    <Stack gap="200" data-test-join-request-expired>
      <Heading level="1" size="m">
        Link expired
      </Heading>
      <Paragraph color="subtle">
        Join links are only valid for a short time after verifying your email. Start again from signup to get a new one.
      </Paragraph>
      <Link href="/signup">Back to signup</Link>
    </Stack>
  );
}
